import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import { IconName } from '@models/IIcon';
import { ILink } from '@models/ILink';
import { IListingResponse } from '@models/IListingResponse';
import { ISearchBlock } from '@models/ISearchBlock';
import { ISearchResult } from '@models/ISearchResult';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import SearchBar from '@molecules/SearchBar/SearchBar';
import SearchSummaryResults from '@molecules/SearchSummaryResults/SearchSummaryResults';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import useSearchSummaryResults from '@utils/hooks/useSearchSummaryResults';
import axios from 'axios';
import classNames from 'classnames';
import { useState } from 'react';
import PopularSearches from './PopularSearches';
import SearchResultsList from './SearchResultsList';

interface Props {
  readonly popularSearches?: { title: string; links: ILink[] };
  readonly listingPage: string;
  readonly searchSubTitle: string;
  readonly searchPlaceholder: string;
  readonly overlaySearchIcon: IconName;
  readonly culture: string;
  readonly endpoint: string;
  readonly suggestedSearchTitleLabel: string;
  readonly searchBlock?: ISearchBlock;
}

const Search = ({
  popularSearches,
  listingPage,
  searchSubTitle,
  searchPlaceholder,
  overlaySearchIcon,
  culture,
  endpoint,
  suggestedSearchTitleLabel,
  searchBlock,
}: Props) => {
  const [value, setValue] = useState('');
  const [results, setResults] = useState<ISearchResult[]>([]);
  const [searchBarContainerRef, { clientHeight: searchBarContainerHeight }] =
    useResizeObserver();

  const { setErrorBannerNotification } = useErrorNotificationStore();

  const fetchResults = async () => {
    try {
      const { data } = await axios.get<IListingResponse | undefined>(endpoint, {
        params: { pageSize: 10, query: value, language: culture },
      });

      setResults((data?.items ?? []) as ISearchResult[]);
    } catch {
      setErrorBannerNotification(ErrorBannerNotificationEnum.SearchResults);
    }
  };

  const { isLoading, searchSummaryResults, fetchSearchSummaryResults } =
    useSearchSummaryResults(searchBlock?.searchSummary ?? []);

  return (
    <>
      <div
        ref={searchBarContainerRef}
        className="flex h-[24%] min-h-[140px] w-full items-center justify-start bg-grey-200 md:h-[48%] md:min-h-[160px] md:pb-7.5"
        data-testid="CT-48-SearchBar-Container"
      >
        <div className="container relative max-md:mt-1">
          <SearchBar
            searchSubTitle={searchSubTitle}
            searchPlaceholder={searchPlaceholder}
            overlaySearchIcon={overlaySearchIcon}
            listingPage={searchBlock?.searchSummaryPageLink ?? listingPage}
            value={value}
            setValue={setValue}
            onTypingDone={() => {
              if (value.length >= 1) {
                if (searchBlock) fetchSearchSummaryResults(value);
                else fetchResults();
              }
            }}
          />
        </div>
      </div>

      <div
        style={
          searchBlock
            ? { minHeight: `calc(100% - ${searchBarContainerHeight}px)` }
            : { height: `calc(100% - ${searchBarContainerHeight}px)` }
        }
        className={classNames(
          'w-full bg-white',
          searchBlock ? 'flex' : 'overflow-y-auto overscroll-y-none'
        )}
      >
        {!value && popularSearches && (
          <div className="container grid grid-cols-12 pt-5 lg:pt-[70px]">
            <PopularSearches
              title={popularSearches.title}
              links={popularSearches.links}
            />
          </div>
        )}

        {!!value &&
          !searchBlock &&
          results.some((result) => result.title && result.url) && (
            <div className="container">
              <SearchResultsList
                results={results}
                suggestedSearchTitleLabel={suggestedSearchTitleLabel}
              />
            </div>
          )}

        {value && isLoading && searchBlock && (
          <div className="flex w-full items-center justify-center">
            <LoadingSpinner />
          </div>
        )}

        {value && !isLoading && searchBlock && (
          <div className="w-full">
            <SearchSummaryResults
              searchSummaryResults={searchSummaryResults}
              searchValue={value}
              productsStatusLabels={searchBlock.statusLabels}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Search;
